import { cn } from '@plato/ui/util/classes';
import { SearchIcon, XIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import { Input } from '../ui/input.tsx';

type Props = {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    inputClassName?: string;
    'aria-label'?: string;
};

export function CancellableSearchBox({
    placeholder = '',
    value: initialValue = '',
    onChange,
    className,
    'aria-label': ariaLabel,
}: Props) {
    const [searchBoxText, setSearchBoxText] = useState(initialValue);

    // Only sync when value is cleared externally (e.g., Clear filters button)
    useEffect(() => {
        if (!initialValue) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- legacy code
            setSearchBoxText('');
        }
    }, [initialValue]);

    const onCancel = () => {
        setSearchBoxText('');
    };

    const showCancel = searchBoxText.length > 0;

    useDebounce(
        () => {
            onChange(searchBoxText);
        },
        500,
        [searchBoxText],
    );

    return (
        // A zero-height copy of the placeholder, padded like the input, gives the box a
        // `fit-content` minimum, so the placeholder is never cut off. It is not rendered
        // below `md`, where the box is full width and truncates as it always has.
        <div className={cn('md:min-w-fit', className)}>
            <span
                aria-hidden="true"
                className="hidden h-0 min-w-56 overflow-hidden pr-3 pl-7 text-sm whitespace-pre md:block"
            >
                {placeholder}
            </span>
            <Input
                leftIcon={SearchIcon}
                rightIcon={showCancel ? XIcon : undefined}
                onRightIconClick={
                    showCancel
                        ? () => {
                              onCancel();
                          }
                        : undefined
                }
                placeholder={placeholder}
                value={searchBoxText}
                onChange={(e) => {
                    setSearchBoxText(e.target.value);
                }}
                className="[&_input::placeholder]:text-sm"
                aria-label={ariaLabel}
            />
        </div>
    );
}
